// Foundations
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Services
import { AnalyticsService } from '@app/services/analytics.service';

// Models
import { ParsedPet } from '@app/models/pet/pet.model';
import { PetStatusType } from '@app/models/pet/pet-status.model';
import {
  ParsedPetVaccine,
  VaccineStatusEnum,
} from '@app/models/pet/pet-vaccine.model';
import { ParsedBooking } from '@app/models/booking.model';

@Component({
  selector: 'app-camper-tile',
  templateUrl: './camper-tile.component.html',
  styleUrls: ['./camper-tile.component.scss'],
})
export class CamperTileComponent implements OnInit {
  @Input() petStatusData: ParsedPet;
  @Input() petImage: string;
  upcomingInterviews = [];
  upcomingBookings = [];
  vaccineStatusEnum = VaccineStatusEnum;

  get PetStatusType() {
    return PetStatusType;
  }

  constructor(private router: Router, private _analytics: AnalyticsService) {}

  ngOnInit(): void {
    this.petStatusData.vaccines.forEach(this.setVaccineStatus);
    // filter the bookings into interviews and bookings
    this.petStatusData.upcomingBookings.sort((book1, book2) =>
      book1.startDate > book2.startDate ? 1 : -1
    );
    for (const booking of this.petStatusData.upcomingBookings) {
      if (booking.categories.includes('Interview')) {
        this.upcomingInterviews.push(booking);
      }
      // include interviews in upcoming bookings
      this.upcomingBookings.push(booking);
    }
    // check reservations and waitlists for interviews
    const nonUpcomingBookings = this.petStatusData.reservationRequests.concat(
      this.petStatusData.waitlistBookings
    );
    for (const booking of nonUpcomingBookings) {
      if (booking.categories.includes('Interview')) {
        this.upcomingInterviews.push(booking);
      }
    }
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get petName() {
    const { firstName, lastName, nickName } = this.petStatusData;
    // CBW-784
    return firstName ? firstName + ' ' + lastName : nickName;
  }

  get petId() {
    return parseInt(this.petStatusData.id, 10);
  }

  getBookingTitle(booking: ParsedBooking) {
    return booking.title
      ? booking.title.charAt(0) === '.'
        ? // remove leading period
          booking.title.substring(1)
        : booking.title
      : booking.categories.join(', ');
  }

  navigateToDogProfile() {
    this._analytics.logButtonTap('landing_viewEditPet');
    this.router.navigate(['/camper-profile', this.petStatusData.id], {
      queryParams: {
        unverifiedDocuments:
          this.petStatusData.numberOfUnverifiedPetVaccinations,
      },
    });
  }

  navigateToInterviewRequest() {
    this._analytics.logButtonTap('landing_requestInterview');
    this.router.navigate(['/interview-request'], {
      queryParams: { petId: this.petStatusData.id },
    });
  }

  navigateToReservationRequest() {
    this._analytics.logButtonTap('landing_requestInterview');
    this.router.navigate(['/reservation'], {
      queryParams: { petId: this.petStatusData.id },
    });
  }

  showVaccineSection(): boolean {
    // if none of the vaccines have vaccinationExpirationState > 0 (i.e none are expiring soon or exipired), return false
    for (const vaccine of this.petStatusData.vaccines) {
      if (vaccine.vaccinationExpirationState > 0) {
        return true;
      }
    }
    return false;
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private setVaccineStatus(vaccine: ParsedPetVaccine) {
    const today = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setDate(oneMonthFromNow.getDate() + 30);
    const exp = new Date(vaccine.expiresOn);

    if (today.getTime() > exp.getTime()) {
      vaccine.vaccinationExpirationState = VaccineStatusEnum.Expired;
    } else if (oneMonthFromNow.getTime() > exp.getTime()) {
      vaccine.vaccinationExpirationState = VaccineStatusEnum.ExpiringSoon;
    } else {
      vaccine.vaccinationExpirationState = VaccineStatusEnum.UpToDate;
    }
  }
}
